/**
 * <CircularProgress> — a circular progress ring (atom, L0.1).
 *
 * A ring whose colored arc represents a 0–maxValue value, with an optional centered label +
 * sublabel (e.g. a "72" score over "/100"). The maison replacement for the vendor
 * `react-native-circular-progress-indicator` used by the legacy live-report card.
 *
 * Le libellé se règle sur le diamètre de l'anneau, qui le choisit lui-même : `text-2xl` à partir
 * de 80 (le défaut), `text-xl` d'un grand anneau (`isLargeRing`, 52 et plus), `text-md` en deçà.
 *
 * Drawn with `react-native-svg` (an allowed DS import). No hex literals: `color` / `trackColor`
 * are `SemanticColorKey` tokens; SVG strokes need a plain string, so they are resolved from the
 * runtime theme via `useUnistyles()` (never a direct token-module import).
 */
import React from "react"
import { View } from "react-native"
import Svg, { Circle } from "react-native-svg"
import { StyleSheet, useUnistyles } from "react-native-unistyles"

import { textInkOnFill, type SemanticColorKey } from "../../theme/tokens/colors"
import { type FontSizeKey } from "../../theme/tokens/typography"
import { Text } from "../typography/Text"

export type CircularProgressProps = {
    /** Current value (clamped to 0–`maxValue`). */
    value: number
    /** Value at a full ring — defaults to 100. */
    maxValue?: number
    /** Ring diameter in px — defaults to 80. */
    size?: number
    /** Arc/track thickness in px — defaults to 5. */
    strokeWidth?: number
    /** Arc color token — defaults to `primary`. */
    color?: SemanticColorKey
    /** Track (background ring) color token — defaults to `border`. */
    trackColor?: SemanticColorKey
    /** Big centered label (e.g. the score), sized to the ring. */
    label?: string
    /** Small centered sub-label under `label` (e.g. "/100"). */
    sublabel?: string
    testID?: string
}

/** Le diamètre par défaut : l'anneau d'une jauge seule. */
export const DEFAULT_RING_SIZE = 80

/** À partir de ce diamètre, l'anneau est « grand » : son libellé passe de `text-md` à `text-xl`. */
const LARGE_RING_FROM = 52

/** Vrai pour un grand anneau — ce qui l'accompagne (le mot d'une jauge) suit la même marche. */
export function isLargeRing(size: number): boolean {
    return size >= LARGE_RING_FROM
}

function labelSizeFor(size: number): FontSizeKey {
    if (size >= DEFAULT_RING_SIZE) return "text-2xl"
    return isLargeRing(size) ? "text-xl" : "text-md"
}

export function CircularProgress(props: CircularProgressProps) {
    const {
        value,
        maxValue = 100,
        size = DEFAULT_RING_SIZE,
        strokeWidth = 5,
        color = "primary",
        trackColor = "border",
        label,
        sublabel,
        testID,
    } = props

    const { theme } = useUnistyles()

    const radius = (size - strokeWidth) / 2
    const circumference = 2 * Math.PI * radius
    const pct = maxValue > 0 ? Math.max(0, Math.min(1, value / maxValue)) : 0
    const dashoffset = circumference * (1 - pct)
    const center = size / 2

    return (
        <View testID={testID} style={styles.wrapper(size)}>
            <Svg width={size} height={size}>
                <Circle
                    cx={center}
                    cy={center}
                    r={radius}
                    stroke={theme.colors[trackColor]}
                    strokeWidth={strokeWidth}
                    fill="none"
                />
                <Circle
                    cx={center}
                    cy={center}
                    r={radius}
                    stroke={theme.colors[color]}
                    strokeWidth={strokeWidth}
                    fill="none"
                    strokeLinecap="round"
                    strokeDasharray={circumference}
                    strokeDashoffset={dashoffset}
                    transform={`rotate(-90 ${center} ${center})`}
                />
            </Svg>
            {(label != null || sublabel != null) && (
                <View style={styles.overlay}>
                    {label != null && (
                        <Text size={labelSizeFor(size)} weight="extrabold" color={textInkOnFill(color)}>
                            {label}
                        </Text>
                    )}
                    {sublabel != null && (
                        <Text size="text-xs" color="text-muted">
                            {sublabel}
                        </Text>
                    )}
                </View>
            )}
        </View>
    )
}

const styles = StyleSheet.create(() => ({
    wrapper: (size: number) => ({
        width: size,
        height: size,
        justifyContent: "center" as const,
        alignItems: "center" as const,
    }),
    overlay: {
        position: "absolute" as const,
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        justifyContent: "center" as const,
        alignItems: "center" as const,
    },
}))
